import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { cn } from '../../lib/cn';
import WordHighlightMenu from '../vocabulary/WordHighlightMenu';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Check if content looks like markdown (has markdown syntax)
function looksLikeMarkdown(text) {
  if (!text) return false;
  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m,           // Headers
    /\*\*[^*]+\*\*/,        // Bold
    /\*[^*]+\*/,            // Italic
    /^[-*+]\s/m,            // Unordered lists
    /^\d+\.\s/m,            // Ordered lists
    /^>\s/m,                // Blockquotes
    /\[.+\]\(.+\)/,         // Links
    /^---$/m,               // Horizontal rules
    /```/,                  // Code blocks
  ];
  return markdownPatterns.some(pattern => pattern.test(text));
}

export default function ArticleDisplay({
  title,
  subtitle,
  content,
  estimatedReadTime,
  className,
  vocabProps = null,
}) {
  const { t } = useTranslation();
  const proseClasses = cn(
    'prose prose-lg max-w-none dark:prose-invert',
    'prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-white',
    'prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4',
    'prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3',
    'prose-p:text-gray-700 prose-p:leading-relaxed dark:prose-p:text-gray-300',
    'prose-p:mb-6 prose-p:max-w-[65ch]',
    'prose-strong:text-gray-900 prose-strong:font-semibold dark:prose-strong:text-white',
    'prose-blockquote:border-l-gray-300 prose-blockquote:text-gray-600 dark:prose-blockquote:border-l-gray-600 dark:prose-blockquote:text-gray-400',
    'prose-blockquote:italic prose-blockquote:pl-6',
    'prose-ul:text-gray-700 prose-ol:text-gray-700 dark:prose-ul:text-gray-300 dark:prose-ol:text-gray-300',
    'prose-li:mb-2',
    '[&_.lead]:text-xl [&_.lead]:text-gray-600 [&_.lead]:leading-relaxed [&_.lead]:mb-8 dark:[&_.lead]:text-gray-400'
  );

  // Parse markdown to HTML if content looks like markdown
  const htmlContent = useMemo(() => {
    if (!content) return '';
    if (looksLikeMarkdown(content)) {
      return marked.parse(content);
    }
    // If not markdown, wrap plain text in paragraphs
    return content;
  }, [content]);

  return (
    <article
      className={cn(
        'bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900',
        'rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.3)]',
        'overflow-hidden',
        className
      )}
    >
      <div className="px-8 py-10 sm:px-12 sm:py-12 lg:px-16 lg:py-14">
        <header className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg text-gray-600 dark:text-gray-400">{subtitle}</p>
          )}
          {estimatedReadTime && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-4">
              {t('stimulus.minRead', { minutes: estimatedReadTime })}
            </p>
          )}
          <div className="w-16 h-px bg-gray-300 dark:bg-gray-600 mx-auto mt-6" />
        </header>

        {vocabProps ? (
          <WordHighlightMenu {...vocabProps}>
            <div
              className={proseClasses}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </WordHighlightMenu>
        ) : (
          <div
            className={proseClasses}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}
      </div>
    </article>
  );
}

export function ArticleSkeleton() {
  return (
    <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden animate-pulse">
      <div className="px-8 py-10 sm:px-12 sm:py-12 lg:px-16 lg:py-14">
        <div className="text-center mb-10">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mx-auto mb-3" />
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto" />
          <div className="w-16 h-px bg-gray-200 dark:bg-gray-700 mx-auto mt-6" />
        </div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-11/12" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mt-8" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10/12" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        </div>
      </div>
    </div>
  );
}
